import { useCallback, useEffect } from 'react';
import { useParams } from 'react-router';

import Auth from '../../../../api/auth';
import Table from '../../../../components/Table';
import { userRolesLookup } from '../../../../helpers/lookups';
import useApiQuery from '../../../../hooks/useApiQuery';
import rbacCheck from '../../../../rbac/rbacCheck';

import { useUserData } from '@/redux/selectors';
import type { APIHookArg, ErrorType } from '@/types';
import type { Product } from '@/types/productTypes';
import type { Project } from '@/types/projectTypes';
import type { User } from '@/types/user';

interface UserAssignmentPayload {
  userId: string;
  productId: string;
}

export interface UsersProps {
  product: Product;
}

const Users: React.FC<UsersProps> = ({ product }) => {
  const { productId: paramsProductId } = useParams<{ productId?: string }>();
  const productId = paramsProductId ?? product._id;
  const userData = useUserData();

  const activatedProduct = (productIds?: string[]): boolean =>
    productIds?.includes(productId) ?? false;

  const {
    data: users = [],
    error: fetchError,
    makeApiCall: getUserByProductIdCall,
  } = useApiQuery<User[]>(
    Auth.fetchV2UsersByProductId as unknown as (args: unknown) => Promise<User[]>
  );
  const { data: project, makeApiCall: getProjectCall } = useApiQuery<Project>(
    Auth.fetchV2ProjectById as unknown as (args: unknown) => Promise<Project>
  );
  const { makeApiCall: assignProductCall } = useApiQuery(
    Auth.assignV2Product as unknown as APIHookArg
  );
  const { makeApiCall: unassignProductCall } = useApiQuery(
    Auth.unassignV2Product as unknown as APIHookArg
  );

  const canAssignProduct = rbacCheck(userData.roles, '/products:assign') as boolean;

  const getUsers = useCallback(async (): Promise<void> => {
    await getUserByProductIdCall(productId);
  }, [getUserByProductIdCall, productId]);

  const getProject = useCallback(async (): Promise<void> => {
    await getProjectCall(product.projectId);
  }, [getProjectCall, product.projectId]);

  const userAssignment = async (user: User, userId: string): Promise<void> => {
    const payload: UserAssignmentPayload = { userId, productId };

    if (activatedProduct(user.productIds)) {
      await unassignProductCall(payload);
    } else {
      await assignProductCall(payload);
    }
    await getUsers();
  };

  useEffect(() => {
    getUsers();
    getProject();
  }, [getUsers, getProject]);

  const fields = [
    { id: 'email', title: 'Email' },
    { id: 'role', title: 'Role' },
    { id: 'status', title: 'Status', type: 'options' },
  ];

  const rows = users.map(user => ({
    key: user._id,
    data: [
      { field: 'email', value: user.email },
      { field: 'role', value: userRolesLookup[user.role] },
      {
        field: 'status',
        type: 'switch',
        value: activatedProduct(user.productIds) ? 'Active' : 'Inactive',
        props: {
          disabled: !canAssignProduct && userData._id !== project?.technicalLeadId,
          checked: activatedProduct(user.productIds),
          label: activatedProduct(user.productIds) ? 'Active' : 'Inactive',
          name: user.email,
          onChange: () => userAssignment(user, user._id),
        },
      },
    ],
  }));

  return (
    <Table
      fields={fields}
      rows={rows}
      error={fetchError as ErrorType}
    />
  );
};

export default Users;
